import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Autocomplete,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { Search, Visibility, FilterList, Close, Delete, Add as AddIcon } from '@mui/icons-material';
import { customerInvoiceService } from '../services/customerInvoiceService';
import { orderService } from '../services/orderService';
import { formatCurrency } from '../components/CurrencyTextField';
import ResizableHeaderCell from '../components/ResizableHeaderCell';
import { procurementSummaryFieldsRowSx } from '../components/procurement/ProcurementSummaryBar';
import { useResizableColumns } from '../hooks/useResizableColumns';
import usePersistedListFilters from '../hooks/usePersistedListFilters';
import { formatDateOnly, parseLocalDate, formatLocalTodayYmd } from '../utils/dateUtils';

const INVOICE_LIST_HEADER_FILTERS_INITIAL = {
  invNo: '',
  customer: '',
  orderNo: '',
  invDate: '',
  dueDate: '',
  total: '',
  amtPaid: '',
  balDue: '',
  status: ''
};

const INVOICE_LIST_FILTERS_DEFAULTS = {
  tableSearch: '',
  statusFilter: '',
  headerColFilters: { ...INVOICE_LIST_HEADER_FILTERS_INITIAL },
  sortKey: 'invoice_number',
  sortDirection: 'desc'
};

const colHeaderStackSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 0.375,
  width: '100%',
  pr: 0
};

const colHeaderLabelSlotSx = {
  flexShrink: 0,
  width: '100%',
  minHeight: '1.5rem',
  overflow: 'hidden',
  '& .MuiTableSortLabel-root': {
    maxWidth: '100%',
    minWidth: 0,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.25,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.25,
    '& .MuiTableSortLabel-icon': { flexShrink: 0 }
  }
};

const headerFilterTextFieldSx = {
  width: '100%',
  flexShrink: 0,
  '& .MuiInputBase-root': { fontSize: '0.7rem', py: 0.1 },
  '& .MuiInputBase-input': { py: 0.35 }
};

function includesFilter(haystack, needle) {
  const n = String(needle ?? '').trim().toLowerCase();
  if (!n) return true;
  return String(haystack ?? '').toLowerCase().includes(n);
}

const CustomerInvoices = () => {
  const DEFAULT_COL_WIDTHS = {
    invNo: 140,
    customer: 220,
    orderNo: 130,
    invDate: 120,
    dueDate: 120,
    total: 120,
    amtPaid: 120,
    balDue: 120,
    status: 120,
    actions: 110
  };
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const partFilter = searchParams.get('part_id');
  const {
    values: listFilters,
    setField: setListFilter,
    clear: clearListFilters,
    hasActiveFilters
  } = usePersistedListFilters('wm_customer_invoices_list_filters', INVOICE_LIST_FILTERS_DEFAULTS);
  const tableSearch = listFilters.tableSearch;
  const setTableSearch = useCallback((v) => setListFilter('tableSearch', v), [setListFilter]);
  const statusFilter = listFilters.statusFilter;
  const setStatusFilter = useCallback((v) => setListFilter('statusFilter', v), [setListFilter]);
  const headerColFilters = listFilters.headerColFilters;
  const setHeaderColFilters = useCallback((v) => setListFilter('headerColFilters', v), [setListFilter]);
  const sortKey = listFilters.sortKey;
  const setSortKey = useCallback((v) => setListFilter('sortKey', v), [setListFilter]);
  const sortDirection = listFilters.sortDirection;
  const setSortDirection = useCallback((v) => setListFilter('sortDirection', v), [setListFilter]);
  const handleClearFilters = useCallback(() => {
    clearListFilters();
    if (partFilter) {
      const next = new URLSearchParams(searchParams);
      next.delete('part_id');
      setSearchParams(next, { replace: true });
    }
  }, [clearListFilters, partFilter, searchParams, setSearchParams]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [orderSearch, setOrderSearch] = useState('');
  const [orderOptions, setOrderOptions] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const { colWidths, startResize } = useResizableColumns({
    defaultWidths: DEFAULT_COL_WIDTHS,
    storageKey: 'wm_customer_invoices_col_widths',
    minWidths: {
      invNo: 110,
      customer: 150,
      orderNo: 100,
      invDate: 100,
      dueDate: 100,
      total: 100,
      amtPaid: 100,
      balDue: 100,
      status: 100,
      actions: 90
    }
  });

  const loadInvoices = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        search: tableSearch || undefined,
        status: statusFilter || undefined,
        ...(partFilter ? { part_id: partFilter } : {}),
        limit: 200
      };
      const response = await customerInvoiceService.getAll(params);
      const list = response.data || [];
      setInvoices(list);
      setSelectedInvoice(list.length > 0 ? list[0] : null);
    } catch (err) {
      console.error('Error loading customer invoices:', err);
      setError(err.response?.data?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, [statusFilter, partFilter]);

  function invoiceBalanceDue(inv) {
    const total = Number(inv?.total_amount) || 0;
    const paid = Number(inv?.amount_paid) || 0;
    return Math.max(0, total - paid);
  }

  const isDueToday = (inv) => {
    const due = parseLocalDate(inv?.due_date);
    if (!due) return false;
    const today = parseLocalDate(formatLocalTodayYmd());
    if (!today) return false;
    due.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return due.getTime() === today.getTime();
  };

  const filteredInvoices = useMemo(() => {
    let list = invoices;
    const lower = (s) => String(s ?? '').toLowerCase();
    const searchLower = lower(tableSearch).trim();
    if (searchLower) {
      list = list.filter((inv) => {
        const invNum = lower(inv.identifier ?? inv.invoice_number ?? '');
        const cust = lower(inv.company_name || `${inv.customer_first_name || ''} ${inv.customer_last_name || ''}`);
        const orderNum = lower(String(inv.work_order_identifier ?? inv.work_order_number ?? ''));
        return invNum.includes(searchLower) || cust.includes(searchLower) || orderNum.includes(searchLower);
      });
    }
    const f = headerColFilters;
    list = list.filter((inv) => {
      const cust = inv.company_name || `${inv.customer_first_name || ''} ${inv.customer_last_name || ''}`.trim();
      const bal = invoiceBalanceDue(inv);
      const status = String(inv?.status || '');
      let statusLabel = status || 'Due';
      if (status === 'Voided' || inv?.voided_at) statusLabel = 'Voided';
      else if (status === 'Paid') statusLabel = 'Paid';
      else if (status === 'Past Due') statusLabel = 'Past Due';
      else if (isDueToday(inv)) statusLabel = 'Due today';
      return (
        includesFilter(inv.identifier ?? inv.invoice_number, f.invNo) &&
        includesFilter(cust, f.customer) &&
        includesFilter(inv.work_order_identifier ?? inv.work_order_number, f.orderNo) &&
        includesFilter(formatDateOnly(inv.invoice_date, 'MM/dd/yyyy') || inv.invoice_date, f.invDate) &&
        includesFilter(formatDateOnly(inv.due_date, 'MM/dd/yyyy') || inv.due_date, f.dueDate) &&
        includesFilter(inv.total_amount, f.total) &&
        includesFilter(inv.amount_paid, f.amtPaid) &&
        includesFilter(bal, f.balDue) &&
        includesFilter(statusLabel, f.status)
      );
    });
    return list;
  }, [invoices, tableSearch, headerColFilters]);

  const handleSort = (key) => {
    setSortDirection((prev) => (sortKey === key ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortKey(key);
  };

  const displayedInvoices = useMemo(() => {
    const list = [...filteredInvoices];
    const getter = (row) => {
      switch (sortKey) {
        case 'invoice_number': return row.identifier ?? row.invoice_number ?? '';
        case 'customer': return row.company_name || `${row.customer_first_name || ''} ${row.customer_last_name || ''}`.trim();
        case 'work_order_number': return row.work_order_identifier ?? row.work_order_number ?? '';
        case 'invoice_date': return row.invoice_date ? (parseLocalDate(row.invoice_date)?.getTime() ?? 0) : 0;
        case 'due_date': return row.due_date ? (parseLocalDate(row.due_date)?.getTime() ?? 0) : 0;
        case 'total_amount': return Number(row.total_amount) || 0;
        case 'amount_paid': return Number(row.amount_paid) || 0;
        case 'balance_due': return invoiceBalanceDue(row);
        case 'status': return row.status ?? '';
        default: return '';
      }
    };
    const dir = sortDirection === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const va = getter(a);
      const vb = getter(b);
      if (typeof va === 'number' && typeof vb === 'number') return dir * (va - vb);
      return dir * String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    });
    return list;
  }, [filteredInvoices, sortKey, sortDirection]);

  const invoiceStatusDisplay = (inv) => {
    const status = String(inv?.status || '');
    if (status === 'Voided' || inv?.voided_at) {
      return { label: 'Voided', sx: { bgcolor: 'grey.600', color: '#fff' } };
    }
    if (status === 'Paid') return { label: 'Paid', sx: { bgcolor: 'success.main', color: '#fff' } };
    if (status === 'Past Due') return { label: 'Past Due', sx: { bgcolor: 'error.main', color: '#fff' } };
    if (isDueToday(inv)) return { label: 'Due today', sx: { bgcolor: '#fdd835', color: '#111', fontSize: '0.68rem' } };
    return { label: status || 'Due', sx: { bgcolor: 'info.main', color: '#fff' } };
  };

  /** Open invoice with meaningful payment applied but not fully paid (matches ~$0.02 tolerance). */
  const invoiceIsPartiallyPaid = (inv) => {
    if (!inv || inv.status === 'Paid') return false;
    const total = Number(inv.total_amount) || 0;
    const paid = Number(inv.amount_paid) || 0;
    if (total <= 0) return false;
    return paid > 0.02 && paid < total - 0.02;
  };

  const renderInvoiceStatusChips = (inv) => {
    const main = invoiceStatusDisplay(inv);
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
        <Chip
          label={main.label}
          size="small"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            ...main.sx
          }}
        />
      {invoiceIsPartiallyPaid(inv) ? (
        <Chip
          label="Partial"
          size="small"
          sx={{
            backgroundColor: '#ed6c02',
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 24,
            '& .MuiChip-label': { px: 0.75 }
          }}
        />
      ) : null}
      </Box>
    );
  };

  const formatDate = (dateString) => {
    return formatDateOnly(dateString, 'MM/dd/yyyy') || '—';
  };

  const renderOrderLink = (invoice) => {
    const orderLabel = invoice?.work_order_identifier ?? invoice?.work_order_number ?? '—';
    if (!invoice?.work_order_id) return orderLabel;
    return (
      <Button
        size="small"
        variant="text"
        sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/work-orders/${invoice.work_order_id}`);
        }}
      >
        {orderLabel}
      </Button>
    );
  };

  const renderCustomerLink = (invoice) => {
    const customerLabel = invoice?.company_name ||
      [invoice?.customer_first_name, invoice?.customer_last_name].filter(Boolean).join(' ') ||
      '—';
    if (!invoice?.customer_id) return customerLabel;
    return (
      <Button
        size="small"
        variant="text"
        sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/customers/${invoice.customer_id}`);
        }}
      >
        {customerLabel}
      </Button>
    );
  };

  const handleDeleteClick = (invoice) => {
    setInvoiceToDelete(invoice);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    if (deleting) return;
    setDeleteDialogOpen(false);
    setInvoiceToDelete(null);
  };

  const confirmDeleteInvoice = async () => {
    if (!invoiceToDelete?.id) return;
    try {
      setDeleting(true);
      setError('');
      await customerInvoiceService.delete(invoiceToDelete.id);
      handleDeleteDialogClose();
      await loadInvoices();
    } catch (err) {
      console.error('Error deleting customer invoice:', err);
      setError(err.response?.data?.message || 'Failed to delete invoice');
    } finally {
      setDeleting(false);
    }
  };

  const openCreateDialog = async () => {
    setCreateDialogOpen(true);
    setOrderSearch('');
    setSelectedOrder(null);
    try {
      const res = await orderService.getAll({ limit: 50, status: 'Open' });
      setOrderOptions(res?.data ?? []);
    } catch (err) {
      console.error('Error loading orders for invoice creation:', err);
      setError(err.response?.data?.message || 'Failed to load orders');
      setOrderOptions([]);
    }
  };

  const closeCreateDialog = () => {
    if (creatingInvoice) return;
    setCreateDialogOpen(false);
    setOrderSearch('');
    setSelectedOrder(null);
  };

  const handleCreateInvoice = async () => {
    if (!selectedOrder?.id) return;
    try {
      setCreatingInvoice(true);
      setError('');
      const res = await customerInvoiceService.createFromWorkOrder(selectedOrder.id);
      closeCreateDialog();
      await loadInvoices();
      const created = res?.data;
      if (created?.id) navigate(`/customer-invoices/${created.id}`);
    } catch (err) {
      console.error('Error creating customer invoice:', err);
      setError(err.response?.data?.message || 'Failed to create invoice');
    } finally {
      setCreatingInvoice(false);
    }
  };

  const filteredOrderOptions = useMemo(() => {
    const term = String(orderSearch || '').trim().toLowerCase();
    if (!term) return orderOptions;
    return orderOptions.filter((o) => {
      const orderLabel = String(o.identifier ?? o.work_order_number ?? '').toLowerCase();
      const customerLabel = String(o.company_name || `${o.customer_first_name || ''} ${o.customer_last_name || ''}`).toLowerCase();
      const customerPo = String(o.po_number || '').toLowerCase();
      return orderLabel.includes(term) || customerLabel.includes(term) || customerPo.includes(term);
    });
  }, [orderOptions, orderSearch]);

  const orderOptionLabel = (o) => {
    if (!o) return '';
    const orderId = o.identifier ?? o.work_order_number ?? `#${o.id}`;
    const customerName = o.company_name || [o.customer_first_name, o.customer_last_name].filter(Boolean).join(' ');
    if (customerName) return `${orderId} - ${customerName}`;
    return String(orderId);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Customer Invoices
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
        >
          Create Invoice
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>
          {error}
        </Alert>
      )}

      {/* Selected Invoice Information Bar */}
      <Paper sx={{ p: 1.5, mb: 1.5, backgroundColor: 'surfaceMuted.main', border: 1, borderColor: 'borderSubtle.main' }}>
        <Box sx={procurementSummaryFieldsRowSx()}>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Invoice #</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedInvoice?.identifier ?? selectedInvoice?.invoice_number ?? '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer</Typography>
            <Typography variant="body2">{renderCustomerLink(selectedInvoice)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Order #</Typography>
            <Typography variant="body2">{renderOrderLink(selectedInvoice)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer PO</Typography>
            <Typography variant="body2">{selectedInvoice?.po_number || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Invoice Date</Typography>
            <Typography variant="body2">{selectedInvoice ? formatDate(selectedInvoice.invoice_date) : '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Due Date</Typography>
            <Typography variant="body2">{selectedInvoice ? formatDate(selectedInvoice.due_date) : '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Total</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedInvoice ? formatCurrency(selectedInvoice.total_amount, '—') : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Amt Paid</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedInvoice ? formatCurrency(selectedInvoice.amount_paid, '—') : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Status</Typography>
            {renderInvoiceStatusChips(selectedInvoice)}
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton size="small" onClick={() => setSelectedInvoice(null)} disabled={!selectedInvoice} sx={{ ml: 'auto' }} title="Clear selection">
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Paper>

      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search invoice #, customer, order #"
            value={tableSearch}
            onChange={(e) => setTableSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadInvoices()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Due">Due</MenuItem>
              <MenuItem value="Past Due">Past Due</MenuItem>
              <MenuItem value="Paid">Paid</MenuItem>
              <MenuItem value="Voided">Voided</MenuItem>
            </Select>
          </FormControl>
          <Button size="small" variant="outlined" onClick={() => selectedInvoice && navigate(`/customer-invoices/${selectedInvoice.id}`)} disabled={!selectedInvoice}>
            View
          </Button>
          <IconButton size="small" onClick={loadInvoices} title="Search">
            <FilterList />
          </IconButton>
          {(hasActiveFilters || partFilter) ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterList />}
              onClick={handleClearFilters}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Clear filters
            </Button>
          ) : null}
        </Box>
      </Paper>

      <TableContainer
        component={Paper}
        sx={{
          maxHeight: '60vh',
          overflowY: 'scroll',
          scrollbarGutter: 'stable'
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Table
            size="small"
            stickyHeader
            sx={{
              '& .MuiTableCell-head': { verticalAlign: 'top' },
              // MuiTableHead-root + stickyHeader beats ResizableHeaderCell’s own sticky rule (same as Quotes list).
              '& .MuiTableHead-root .MuiTableCell-stickyHeader': {
                top: -13,
                zIndex: 20,
                backgroundColor: 'background.paper',
                backgroundImage: 'none',
                backgroundClip: 'padding-box',
                opacity: 1,
                overflow: 'hidden',
                borderTop: '1px solid',
                borderTopColor: 'background.paper',
                boxShadow: (theme) =>
                  `inset 0 -1px 0 rgba(224,224,224,0.9), 0 -13px 0 0 ${theme.palette.background.paper}`
              }
            }}
          >
            <TableHead>
              <TableRow sx={{ backgroundColor: 'surfaceMuted.main' }}>
                <ResizableHeaderCell width={colWidths.invNo} onResizeStart={(e) => startResize('invNo', e)}>
                  <Box sx={colHeaderStackSx}>
                    <Box sx={colHeaderLabelSlotSx}>
                      <TableSortLabel active={sortKey === 'invoice_number'} direction={sortDirection} onClick={() => handleSort('invoice_number')}>
                        Invoice #
                      </TableSortLabel>
                    </Box>
                    <TextField
                      size="small"
                      placeholder="Filter…"
                      value={headerColFilters.invNo}
                      onChange={(e) => setHeaderColFilters((p) => ({ ...p, invNo: e.target.value }))}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      sx={headerFilterTextFieldSx}
                    />
                  </Box>
                </ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.customer} onResizeStart={(e) => startResize('customer', e)}>
                  <Box sx={colHeaderStackSx}>
                    <Box sx={colHeaderLabelSlotSx}>
                      <TableSortLabel active={sortKey === 'customer'} direction={sortDirection} onClick={() => handleSort('customer')}>
                        Customer
                      </TableSortLabel>
                    </Box>
                    <TextField
                      size="small"
                      placeholder="Filter…"
                      value={headerColFilters.customer}
                      onChange={(e) => setHeaderColFilters((p) => ({ ...p, customer: e.target.value }))}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      sx={headerFilterTextFieldSx}
                    />
                  </Box>
                </ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.orderNo} onResizeStart={(e) => startResize('orderNo', e)}>
                  <Box sx={colHeaderStackSx}>
                    <Box sx={colHeaderLabelSlotSx}>
                      <TableSortLabel active={sortKey === 'work_order_number'} direction={sortDirection} onClick={() => handleSort('work_order_number')}>
                        Order #
                      </TableSortLabel>
                    </Box>
                    <TextField
                      size="small"
                      placeholder="Filter…"
                      value={headerColFilters.orderNo}
                      onChange={(e) => setHeaderColFilters((p) => ({ ...p, orderNo: e.target.value }))}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      sx={headerFilterTextFieldSx}
                    />
                  </Box>
                </ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.invDate} onResizeStart={(e) => startResize('invDate', e)}>
                  <Box sx={colHeaderStackSx}>
                    <Box sx={colHeaderLabelSlotSx}>
                      <TableSortLabel active={sortKey === 'invoice_date'} direction={sortDirection} onClick={() => handleSort('invoice_date')}>
                        Invoice Date
                      </TableSortLabel>
                    </Box>
                    <TextField
                      size="small"
                      placeholder="Filter…"
                      value={headerColFilters.invDate}
                      onChange={(e) => setHeaderColFilters((p) => ({ ...p, invDate: e.target.value }))}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      sx={headerFilterTextFieldSx}
                    />
                  </Box>
                </ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.dueDate} onResizeStart={(e) => startResize('dueDate', e)}>
                  <Box sx={colHeaderStackSx}>
                    <Box sx={colHeaderLabelSlotSx}>
                      <TableSortLabel active={sortKey === 'due_date'} direction={sortDirection} onClick={() => handleSort('due_date')}>
                        Due Date
                      </TableSortLabel>
                    </Box>
                    <TextField
                      size="small"
                      placeholder="Filter…"
                      value={headerColFilters.dueDate}
                      onChange={(e) => setHeaderColFilters((p) => ({ ...p, dueDate: e.target.value }))}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      sx={headerFilterTextFieldSx}
                    />
                  </Box>
                </ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.total} onResizeStart={(e) => startResize('total', e)} align="right">
                  <Box sx={colHeaderStackSx}>
                    <Box sx={colHeaderLabelSlotSx}>
                      <TableSortLabel active={sortKey === 'total_amount'} direction={sortDirection} onClick={() => handleSort('total_amount')}>
                        Total
                      </TableSortLabel>
                    </Box>
                    <TextField
                      size="small"
                      placeholder="Filter…"
                      value={headerColFilters.total}
                      onChange={(e) => setHeaderColFilters((p) => ({ ...p, total: e.target.value }))}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      sx={headerFilterTextFieldSx}
                    />
                  </Box>
                </ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.amtPaid} onResizeStart={(e) => startResize('amtPaid', e)} align="right">
                  <Box sx={colHeaderStackSx}>
                    <Box sx={colHeaderLabelSlotSx}>
                      <TableSortLabel active={sortKey === 'amount_paid'} direction={sortDirection} onClick={() => handleSort('amount_paid')}>
                        Amt Paid
                      </TableSortLabel>
                    </Box>
                    <TextField
                      size="small"
                      placeholder="Filter…"
                      value={headerColFilters.amtPaid}
                      onChange={(e) => setHeaderColFilters((p) => ({ ...p, amtPaid: e.target.value }))}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      sx={headerFilterTextFieldSx}
                    />
                  </Box>
                </ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.balDue} onResizeStart={(e) => startResize('balDue', e)} align="right">
                  <Box sx={colHeaderStackSx}>
                    <Box sx={colHeaderLabelSlotSx}>
                      <TableSortLabel active={sortKey === 'balance_due'} direction={sortDirection} onClick={() => handleSort('balance_due')}>
                        Balance Due
                      </TableSortLabel>
                    </Box>
                    <TextField
                      size="small"
                      placeholder="Filter…"
                      value={headerColFilters.balDue}
                      onChange={(e) => setHeaderColFilters((p) => ({ ...p, balDue: e.target.value }))}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      sx={headerFilterTextFieldSx}
                    />
                  </Box>
                </ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.status} onResizeStart={(e) => startResize('status', e)}>
                  <Box sx={colHeaderStackSx}>
                    <Box sx={colHeaderLabelSlotSx}>
                      <TableSortLabel active={sortKey === 'status'} direction={sortDirection} onClick={() => handleSort('status')}>
                        Status
                      </TableSortLabel>
                    </Box>
                    <TextField
                      size="small"
                      placeholder="Filter…"
                      value={headerColFilters.status}
                      onChange={(e) => setHeaderColFilters((p) => ({ ...p, status: e.target.value }))}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      sx={headerFilterTextFieldSx}
                    />
                  </Box>
                </ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.actions} onResizeStart={(e) => startResize('actions', e)} align="right" sx={{ fontWeight: 600 }}>
                  Actions
                </ResizableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    No invoices found
                  </TableCell>
                </TableRow>
              ) : (
                displayedInvoices.map((inv) => (
                  <TableRow
                    key={inv.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: selectedInvoice?.id === inv.id ? 'action.selected' : 'inherit'
                    }}
                    onClick={() => setSelectedInvoice(inv)}
                    onDoubleClick={() => navigate(`/customer-invoices/${inv.id}`)}
                  >
                    <TableCell sx={{ width: colWidths.invNo, fontWeight: 500 }}>{inv.identifier ?? inv.invoice_number}</TableCell>
                    <TableCell sx={{ width: colWidths.customer }}>
                      {renderCustomerLink(inv)}
                    </TableCell>
                    <TableCell sx={{ width: colWidths.orderNo }}>{renderOrderLink(inv)}</TableCell>
                    <TableCell sx={{ width: colWidths.invDate }}>{formatDate(inv.invoice_date)}</TableCell>
                    <TableCell sx={{ width: colWidths.dueDate }}>{formatDate(inv.due_date)}</TableCell>
                    <TableCell sx={{ width: colWidths.total }} align="right">{formatCurrency(inv.total_amount, '—')}</TableCell>
                    <TableCell sx={{ width: colWidths.amtPaid }} align="right">{formatCurrency(inv.amount_paid, '—')}</TableCell>
                    <TableCell sx={{ width: colWidths.balDue }} align="right">{formatCurrency(invoiceBalanceDue(inv), '—')}</TableCell>
                    <TableCell sx={{ width: colWidths.status }}>{renderInvoiceStatusChips(inv)}</TableCell>
                    <TableCell sx={{ width: colWidths.actions }} align="right" onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/customer-invoices/${inv.id}`)}
                        title="View"
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(inv)}
                        title="Delete"
                        disabled={deleting}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        aria-labelledby="delete-invoice-dialog-title"
      >
        <DialogTitle id="delete-invoice-dialog-title">Delete Invoice</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete invoice {invoiceToDelete?.identifier ?? invoiceToDelete?.invoice_number ?? ''}? Packing slips are not deleted. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} disabled={deleting}>Cancel</Button>
          <Button onClick={confirmDeleteInvoice} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createDialogOpen}
        onClose={closeCreateDialog}
        aria-labelledby="create-invoice-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="create-invoice-dialog-title">Create Invoice</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            Select an order to create an invoice from current order lines.
          </DialogContentText>
          <Autocomplete
            options={filteredOrderOptions}
            value={selectedOrder}
            onChange={(_, value) => setSelectedOrder(value)}
            onInputChange={(_, value) => setOrderSearch(value)}
            getOptionLabel={orderOptionLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Order"
                placeholder="Search order #, customer, or PO"
                size="small"
              />
            )}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog} disabled={creatingInvoice}>Cancel</Button>
          <Button
            onClick={handleCreateInvoice}
            variant="contained"
            disabled={!selectedOrder || creatingInvoice}
          >
            {creatingInvoice ? 'Creating...' : 'Create Invoice'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerInvoices;
